import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './AppDialog.css';

/**
 * @param {{ dialog: object | null, onResolve: (value: unknown) => void }} props
 */
export default function AppDialog({ dialog, onResolve }) {
  const inputRef = useRef(null);
  const [promptValue, setPromptValue] = useState('');

  useEffect(() => {
    if (dialog?.type === 'prompt') {
      setPromptValue(dialog.defaultValue ?? '');
      const t = setTimeout(() => {
        inputRef.current?.focus?.();
        inputRef.current?.select?.();
      }, 0);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [dialog]);

  useEffect(() => {
    if (!dialog) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (dialog.type === 'confirm') onResolve(false);
        else if (dialog.type === 'prompt') onResolve(null);
        else if (dialog.type === 'alert') onResolve();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dialog, onResolve]);

  if (!dialog) return null;

  const handleBackdrop = (e) => {
    if (e.target !== e.currentTarget) return;
    if (dialog.type === 'confirm') onResolve(false);
    else if (dialog.type === 'prompt') onResolve(null);
    else if (dialog.type === 'alert') onResolve();
  };

  const cancelLabel = dialog.cancelLabel || 'Cancel';
  const primaryLabel =
    dialog.confirmLabel ||
    (dialog.type === 'alert' ? 'OK' : 'OK');

  return createPortal(
    <div className="app-dialog-overlay" onClick={handleBackdrop} role="presentation">
      <div
        className="app-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="app-dialog-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="app-dialog-title" className="app-dialog-title">
          {dialog.title}
        </h2>
        {dialog.message ? <p className="app-dialog-message">{dialog.message}</p> : null}

        {dialog.type === 'prompt' ? (
          <label className="app-dialog-input-label">
            {dialog.inputLabel ? (
              <span className="app-dialog-input-caption">{dialog.inputLabel}</span>
            ) : null}
            <input
              ref={inputRef}
              type="text"
              className="app-dialog-input"
              value={promptValue}
              onChange={(e) => setPromptValue(e.target.value)}
              placeholder={dialog.placeholder || ''}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onResolve(promptValue);
                }
              }}
            />
          </label>
        ) : null}

        <div className="app-dialog-actions">
          {dialog.type === 'confirm' || dialog.type === 'prompt' ? (
            <button
              type="button"
              className="app-dialog-btn app-dialog-btn-secondary"
              onClick={() => onResolve(dialog.type === 'confirm' ? false : null)}
            >
              {cancelLabel}
            </button>
          ) : null}
          <button
            type="button"
            className={`app-dialog-btn app-dialog-btn-primary ${dialog.danger ? 'app-dialog-btn-danger' : ''}`}
            onClick={() => {
              if (dialog.type === 'prompt') onResolve(promptValue);
              else if (dialog.type === 'confirm') onResolve(true);
              else onResolve();
            }}
          >
            {primaryLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
